import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import { rowMatchesUnifiedSearch } from "../../shared/unifiedSearch";
import { Button } from "../../components/ui/button";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { Option } from "../../interface/Option";
import { ActivityAllowedICDAxiosInstance } from "../../Network/ActivityAllowedICDAxiosInstance";
import { activityAllowedICD, Datum } from "../../interface/ActivityAllowedIcd";
import ListofActivityCode from "../Shared/ListOfActivityCode";
import ListOfActivityCustom from "../Shared/ListOfActivityCustom";
import ListOfActivityMedical from "../Shared/ListOfActivityMedical";
import Listofmedicalcode from "../Shared/ListofIcdmedicalcode";
import ListofMedicalGroup from "../Shared/ListofMedicalGroup";
import ListofCustomIcd from "../Shared/ListofcustemIcd";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import ListOfInsuranceplanName from "../Shared/ListOfInsuranceplanName";
import VipPatientSelector from "Pages/Shared/VipPatient";
import ListOfProviderName from "../Shared/ListOfProviderName";
import PolicyNames from "../Shared/PolicyNames";
import FileUpload from "Pages/uploadFile/ActivityAllowedICD/ActivityAllowedICD";
import ListOfDoctorName from "../Shared/Doctor";
import Vip from "../Shared/Vip";
import ListOfSpecialtyName from "../Shared/Specialty";

export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  plans?: Array<{ Plan_name?: string; plan_name?: string; name?: string; id?: number; [key: string]: any }>;
}

interface CodeWithType { code: string | number; type: string; }
type ActivitiesByType = { CPT: Option[]; HCPCS: Option[]; Dental: Option[]; Drugs: Option[] };
const EMPTY_BY_TYPE: ActivitiesByType = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };

const TYPE_MAP: Record<string, string> = {
  cpt: "CPT", CPT: "CPT", hcpcs: "HCPCS", HCPCS: "HCPCS",
  dental: "Dental", Dental: "Dental", drugs: "Drugs", Drugs: "Drugs",
};

const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

const getActivityTypeColor = (type: string) => {
  const tw: Record<string, { bg: string; border: string; text: string }> = {
    CPT:    { bg: "bg-blue-100",   border: "border-blue-500",   text: "text-blue-900"   },
    HCPCS:  { bg: "bg-green-100",  border: "border-green-500",  text: "text-green-900"  },
    Dental: { bg: "bg-purple-100", border: "border-purple-500", text: "text-purple-900" },
    Drugs:  { bg: "bg-orange-100", border: "border-orange-500", text: "text-orange-900" },
  };
  return tw[type] || { bg: "bg-gray-100", border: "border-gray-500", text: "text-gray-900" };
};

const flattenByType = (byType: ActivitiesByType): CodeWithType[] => {
  const result: CodeWithType[] = [];
  Object.entries(byType).forEach(([type, options]) => { options.forEach((o) => result.push({ code: o.value as string, type })); });
  return result;
};

const groupByType = (items: CodeWithType[]): ActivitiesByType => {
  const grouped: ActivitiesByType = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
  items.forEach(({ code, type }) => {
    const key = TYPE_MAP[type] as keyof ActivitiesByType;
    if (key) grouped[key].push({ value: code, label: code.toString() });
  });
  return grouped;
};

const preserveScrollCheckbox = (e: React.ChangeEvent<HTMLInputElement>, fn: () => void) => {
  const el = e.currentTarget as HTMLElement;
  const dialog = el.closest('[role="dialog"]');
  const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
  const scrollTop = scrollable?.scrollTop ?? 0;
  fn();
  requestAnimationFrame(() => { if (scrollable) scrollable.scrollTop = scrollTop; });
};

const cardColors: Record<string, { border: string; bg: string; text: string; badgeBg: string }> = {
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    text: "text-blue-700",    badgeBg: "bg-blue-50 text-blue-700 border-blue-200" },
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50",    text: "text-cyan-700",    badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  purple:  { border: "border-purple-200",  bg: "bg-purple-50",  text: "text-purple-700",  badgeBg: "bg-purple-50 text-purple-700 border-purple-200" },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-700",   badgeBg: "bg-amber-50 text-amber-700 border-amber-200" },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pink:    { border: "border-pink-200",    bg: "bg-pink-50",    text: "text-pink-700",    badgeBg: "bg-pink-50 text-pink-700 border-pink-200" },
  yellow:  { border: "border-yellow-200",  bg: "bg-yellow-50",  text: "text-yellow-700",  badgeBg: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50",  text: "text-indigo-700",  badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  slate:   { border: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-700",   badgeBg: "bg-slate-50 text-slate-700 border-slate-200" },
  teal:    { border: "border-teal-200",    bg: "bg-teal-50",    text: "text-teal-700",    badgeBg: "bg-teal-50 text-teal-700 border-teal-200" },
};

interface EntityCardProps {
  label: string; icon: string; color: string;
  isActive: boolean; mode: "EXCLUSION" | "INCLUSION" | null;
  onExclusion: () => void; onInclusion: () => void; onClear: () => void;
  children: React.ReactNode;
}

const EntityCard: React.FC<EntityCardProps> = ({ label, icon, color, isActive, mode, onExclusion, onInclusion, onClear, children }) => {
  const c = cardColors[color] ?? cardColors.slate;
  const preserveScroll = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    const dialog = (e.currentTarget as HTMLElement).closest('[role="dialog"]');
    const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
    const scrollTop = scrollable?.scrollTop ?? 0;
    fn();
    requestAnimationFrame(() => { if (scrollable) scrollable.scrollTop = scrollTop; });
  };
  return (
    <div className={`bg-white rounded-xl border p-4 transition-all relative ${isActive ? `${c.border} shadow-sm` : "border-slate-100"}`}>
      {isActive && (
        <button type="button" onClick={preserveScroll(onClear)}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10" title="Clear">
          <i className="fa-solid fa-xmark text-[14px]"></i>
        </button>
      )}
      <div className="flex flex-col items-center gap-4">
        <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isActive ? c.badgeBg : "bg-slate-50 text-slate-400 border-slate-200"}`}>
          <i className={`fa-solid ${icon} text-[12px]`}></i> {label}
        </span>
        <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
          <button type="button" onClick={preserveScroll(onExclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}>
            Exclusion
          </button>
          <button type="button" onClick={preserveScroll(onInclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}>
            Inclusion
          </button>
        </div>
      </div>
      {isActive && <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">{children}</div>}
    </div>
  );
};
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ num, title, subtitle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{num}</span>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const SourcePicker: React.FC<{
  title: string;
  selected: string | null;
  onSelect: (key: string | null) => void;
  options: { key: string; label: string; icon: string; count: number }[];
  children: React.ReactNode;
}> = ({ title, selected, onSelect, options, children }) => (
  <div>
    <Label className="mb-2 block">{title}</Label>
    <div className="flex bg-slate-100 rounded-lg p-1 border border-slate-200 gap-1">
      {options.map((opt) => {
        const isSelected = selected === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onSelect(isSelected ? null : opt.key)}
            className={`flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition-all ${isSelected ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            <i className={`fa-solid ${opt.icon} text-xs`}></i>
            <span>{opt.label}</span>
            {opt.count > 0 && (
              <span className={`text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center ${isSelected ? "bg-blue-100 text-blue-700" : "bg-slate-300 text-slate-600"}`}>
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
    {selected && (
      <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        {children}
      </div>
    )}
  </div>
);
const modeFromString = (val: string | undefined | null): "EXCLUSION" | "INCLUSION" | null => {
  if (val === "EXCLUDED") return "EXCLUSION";
  if (val === "INCLUDED") return "INCLUSION";
  return null;
};

const ActivityCell: React.FC<{
  items: CodeWithType[]; medicalGroups: string[]; customGroups: string[];
  onShowAll: (items: CodeWithType[], medical: string[], custom: string[]) => void;
}> = ({ items, medicalGroups, customGroups, onShowAll }) => {
  const totalCount = items.length + medicalGroups.length + customGroups.length;
  if (totalCount === 0) return <div className="w-full text-center text-gray-400">—</div>;
  const allDisplay = [
    ...items.map(c => ({ label: c.code.toString(), type: c.type, isGroup: false })),
    ...medicalGroups.map(g => ({ label: g, type: 'Medical', isGroup: true })),
    ...customGroups.map(g => ({ label: g, type: 'Custom', isGroup: true })),
  ];
  const display = allDisplay.slice(0, 3);
  const hasMore = allDisplay.length > 3;
  return (
    <div className="w-full px-2 py-1.5">
      <div className="flex flex-wrap justify-center gap-1.5 mb-1.5">
        {display.map((c, idx) => {
          const col = c.isGroup
            ? c.type === 'Medical' ? { bg: 'bg-slate-50', border: 'border-slate-400', text: 'text-slate-700' } : { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800' }
            : getActivityTypeColor(c.type);
          return (
            <div key={idx} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${col.bg} ${col.border}`}>
              <span className={`font-semibold text-xs ${col.text}`}>{c.label}</span>
              <span className={`text-[9px] font-bold uppercase ${col.text} opacity-75`}>{c.type}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between border-t border-gray-100 pt-1">
        <span className="text-xs font-bold text-gray-500">{totalCount}</span>
        {hasMore && <button onClick={() => onShowAll(items, medicalGroups, customGroups)} className="text-xs text-blue-600 hover:text-blue-800 font-semibold">All codes →</button>}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
const ActivityAllowedICD: React.FC = () => {
  const [items, setItems] = useState<activityAllowedICD>({ data: [] });
  const [relationName, setRelationName] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);
  const [activitiesByType, setActivitiesByType] = useState<ActivitiesByType>({ ...EMPTY_BY_TYPE });
  const [ActivityMedicalGroups, setActivityMedicalGroups] = useState<Option[]>([]);
  const [ActivityCustomGroups, setActivityCustomGroups] = useState<Option[]>([]);
  const [selectedAllowedOption, setSelectedAllowedOption] = useState<string | null>(null);
  const [icd, setIcd] = useState<Option[]>([]);
  const [icdMedicalGroups, setIcdMedicalGroups] = useState<Option[]>([]);
  const [icdCustomGroups, setIcdCustomGroups] = useState<Option[]>([]);
  const [IcdDescription] = useState<string>("");
const [groupValidationTick, setGroupValidationTick] = useState<boolean>(false);

  const [provider, setProvider] = useState<Option[]>([]);
  const [providerMode, setProviderMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isProviderActive, setIsProviderActive] = useState(false);
  const [clearProvider, setClearProvider] = useState(false);
  const [selectAllProviders, setSelectAllProviders] = useState(false);

  const [doctor, setDoctor] = useState<Option[]>([]);
  const [doctorMode, setDoctorMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isDoctorActive, setIsDoctorActive] = useState(false);
  const [clearDoctor, setClearDoctor] = useState(false);
  const [selectAllDoctor, setSelectAllDoctor] = useState(false);

  const [specialty, setSpecialty] = useState<Option[]>([]);
  const [specialtyMode, setSpecialtyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isSpecialtyActive, setIsSpecialtyActive] = useState(false);
  const [clearSpecialty, setClearSpecialty] = useState(false);
  const [selectAllSpecialty, setSelectAllSpecialty] = useState(false);

  const [networks, setNetworks] = useState<Option[]>([]);
  const [networkMode, setNetworkMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isNetworkActive, setIsNetworkActive] = useState(false);
  const [clearNetworks, setClearNetworks] = useState(false);
  const [selectAllNetworks, setSelectAllNetworks] = useState(false);

  const [insuranceplan, setInsuranceplan] = useState<Option[]>([]);
  const [insuranceMode, setInsuranceMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isInsuranceActive, setIsInsuranceActive] = useState(false);
  const [clearInsuranceplan, setClearInsuranceplan] = useState(false);
  const [selectAllInsurance, setSelectAllInsurance] = useState(false);

  const [Payer, setPayer] = useState<string[]>([]);
  const [payerMode, setPayerMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPayerActive, setIsPayerActive] = useState(false);
  const [selectAllPayer, setSelectAllPayer] = useState(false);

  const [policy, setPolicy] = useState<PolicyItem[]>([]);
  const [policyMode, setPolicyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPolicyActive, setIsPolicyActive] = useState(false);
  const [clearPolicy, setClearPolicy] = useState(false);
  const [selectAllPolicy, setSelectAllPolicy] = useState(false);

  const [patients, setPatients] = useState<Option[]>([]);
  const [patientMode, setPatientMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPatientActive, setIsPatientActive] = useState(false);
  const [clearPatient, setClearPatient] = useState(false);
  const [selectAllPatients, setSelectAllPatients] = useState(false);

  const [vip, setVip] = useState<Option[]>([]);
  const [vipMode, setVipMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isVipActive, setIsVipActive] = useState(false);
  const [clearVip, setClearVip] = useState(false);
  const [selectAllVip, setSelectAllVip] = useState(false);

  const [userLevel, setUserLevel] = useState<string[]>([]);
  const [userMode, setUserMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isUserActive, setIsUserActive] = useState(false);
  const [selectAllUser, setSelectAllUser] = useState(false);

  const [showEntities, setShowEntities] = useState(false);

  const [clearIcd, setClearIcd] = useState(false);
  const [clearIcdMedicalGroups, setClearIcdMedicalGroups] = useState(false);
  const [clearIcdCustomGroups, setClearIcdCustomGroups] = useState(false);
  const [clearActivityMedicalGroups, setClearActivityMedicalGroups] = useState(false);
  const [clearActivityCustomGroups, setClearActivityCustomGroups] = useState(false);

  const [allCodesMedical, setAllCodesMedical] = useState<string[]>([]);
  const [allCodesCustom, setAllCodesCustom] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterSearch, setFilterSearch] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [allCodesOpen, setAllCodesOpen] = useState(false);
  const [allCodesItems, setAllCodesItems] = useState<CodeWithType[]>([]);
  const [allCodesTitle, setAllCodesTitle] = useState("");
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [icdCodesDialogOpen, setIcdCodesDialogOpen] = useState(false);
  const [icdCodesDialogList, setIcdCodesDialogList] = useState<Array<{ label: string; kind: "icd" | "medical" | "custom" }>>([]);
  const [icdCodesDialogTitle, setIcdCodesDialogTitle] = useState("");
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  // ── Role & Admin ──
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ── Force Dialog ──
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | null>(null);

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

const normalizeItem = (item: any): Datum & { activity_flat: CodeWithType[] } => {
  const actCodes: string[] = item.activity || [];
  const actTypes: string[] = Array.isArray(item.activity_type) ? item.activity_type : [];
  const activity_flat: CodeWithType[] = actCodes.map((code, idx) => ({
    code,
    type: TYPE_MAP[(actTypes[idx] || "cpt").toLowerCase()] || "CPT",
  }));
  return {
    ...item,
    activity_flat,
    networks:  item.networks  ?? item.network  ?? [],
    patients:  item.patients  ?? item.patient  ?? [],
    specialty: item.specialty ?? [],
    apply_to_other_specialty: item.apply_to_other_specialty ?? false,
    select_all_providers:     item.provider_all      ?? false,
    select_all_doctor:        item.doctor_all        ?? false,
    select_all_specialty:     item.specialty_all     ?? false,
    select_all_networks:      item.network_all       ?? false,
    select_all_insuranceplan: item.insuranceplan_all ?? false,
    select_all_payer:         item.payer_all         ?? false,
    select_all_patients:      item.patient_all       ?? false,
    select_all_policy:        item.policy_all        ?? false,
    select_all_user:          item.user_all          ?? false,
    select_all_vip:           item.vip_all           ?? false,
    group_validation_tick:    item.group_validation_tick ?? false,
  };
};


  useEffect(() => { fetchUsers(); }, []);

  const resetAllEntityStates = () => {
    setProvider([]);    setProviderMode(null);  setIsProviderActive(false);  setSelectAllProviders(false);
    setDoctor([]);      setDoctorMode(null);    setIsDoctorActive(false);    setSelectAllDoctor(false);
    setSpecialty([]);   setSpecialtyMode(null); setIsSpecialtyActive(false); setSelectAllSpecialty(false);
    setNetworks([]);    setNetworkMode(null);   setIsNetworkActive(false);   setSelectAllNetworks(false);
    setInsuranceplan([]);setInsuranceMode(null);setIsInsuranceActive(false); setSelectAllInsurance(false);
    setPayer([]);       setPayerMode(null);     setIsPayerActive(false);     setSelectAllPayer(false);
    setPolicy([]);      setPolicyMode(null);    setIsPolicyActive(false);    setSelectAllPolicy(false);
    setPatients([]);    setPatientMode(null);   setIsPatientActive(false);   setSelectAllPatients(false);
    setVip([]);         setVipMode(null);       setIsVipActive(false);       setSelectAllVip(false);
    setUserLevel([]);   setUserMode(null);      setIsUserActive(false);      setSelectAllUser(false);
    setClearProvider(true); setClearDoctor(true);   setClearSpecialty(true);
    setClearNetworks(true); setClearInsuranceplan(true);
    setClearPatient(true);  setClearPolicy(true);   setClearVip(true);
  };

  const handleToggleEntities = () => {
    if (showEntities) resetAllEntityStates();
    setShowEntities((prev) => !prev);
  };

  const validateEntities = (): string | null => {
    const checks = [
      { isActive: isProviderActive,  hasData: selectAllProviders  || provider.length > 0,     label: "Providers" },
      { isActive: isDoctorActive,    hasData: selectAllDoctor     || doctor.length > 0,        label: "Doctor" },
      { isActive: isSpecialtyActive, hasData: selectAllSpecialty  || specialty.length > 0,     label: "Specialty" },
      { isActive: isNetworkActive,   hasData: selectAllNetworks   || networks.length > 0,      label: "Networks" },
      { isActive: isInsuranceActive, hasData: selectAllInsurance  || insuranceplan.length > 0, label: "Insurance Plan" },
      { isActive: isPolicyActive,    hasData: selectAllPolicy     || policy.length > 0,        label: "Policy" },
      { isActive: isPatientActive,   hasData: selectAllPatients   || patients.length > 0,      label: "Patients" },
      { isActive: isVipActive,       hasData: selectAllVip        || vip.length > 0,           label: "VIP" },
      { isActive: isPayerActive,     hasData: selectAllPayer      || Payer.length > 0,         label: "Payer" },
      { isActive: isUserActive,      hasData: selectAllUser       || userLevel.length > 0,     label: "User Level" },
    ];
    const empty = checks.filter(c => c.isActive && !c.hasData).map(c => c.label);
    if (empty.length >= 1) {
      return `The following cards are open but have no data selected: ${empty.join(", ")}. Please select at least one item for each, or close them using the ✕ button.`;
    }
    return null;
  };

  const clear = () => {
    setRelationName("");
    setActivitiesByType({ ...EMPTY_BY_TYPE });
    setActivityMedicalGroups([]);
    setActivityCustomGroups([]);
    setSelectedMainOption(null);
    setIcd([]);
    setIcdMedicalGroups([]);
    setIcdCustomGroups([]);
    setSelectedAllowedOption(null);
    setIsActive(true);
    setShowEntities(false);
    resetAllEntityStates();
    setClearIcd(true);
    setClearIcdMedicalGroups(true);
    setGroupValidationTick(false);
    setClearIcdCustomGroups(true);
    setClearActivityMedicalGroups(true);
    setClearActivityCustomGroups(true);
  };

  const buildProviderValues = (arr: Option[]) =>
    arr.map((o: any) => {
      if (o.providerId && o.providerName) return { Provider_id: o.providerId, Provider_name: o.providerName };
      const match = o.label?.match(/^\((.+)\) (.+)$/);
      if (match) return { Provider_id: match[1].trim(), Provider_name: match[2].trim() };
      return { Provider_id: o.value?.toString() || "", Provider_name: o.label?.toString() || "" };
    });

  const buildPolicyValues = (arr: PolicyItem[]) =>
    arr.map((p: any) => {
      if (typeof p === "object" && p.Policy_id && !p.value) return { id: p.id || 0, Policy_id: p.Policy_id, Policy_name: p.Policy_name, plans: p.plans || [] };
      if (typeof p === "object" && p.value) return { id: p.value.id || 0, Policy_id: p.value.Policy_id || "", Policy_name: p.value.Policy_name || "", plans: p.value.plans || [] };
      if (typeof p === "string") return { id: 0, Policy_id: p, Policy_name: p, plans: [] };
      return { id: 0, Policy_id: "", Policy_name: "", plans: [] };
    });

  const buildPayload = () => {
    const flat = flattenByType(activitiesByType);
    return {
      relation_name:          relationName,
      activity:               flat.map((c) => c.code),
      activity_type:          flat.map((c) => c.type),
      types:                  Array.from(new Set(flat.map((c) => c.type))),
      activity_medicalgroups: ActivityMedicalGroups.map((o) => o.value),
      activity_customgroups:  ActivityCustomGroups.map((o) => o.value),
      icd:                    icd.map((o) => o.value),
      icd_medicalgroups:      icdMedicalGroups.map((o) => o.value),
icd_customgroups:       icdCustomGroups.map((o) => o.value),
group_validation_tick:  groupValidationTick,
      provider:           buildProviderValues(provider),
      provider_mode:      providerMode === "EXCLUSION" ? "EXCLUDED" : providerMode === "INCLUSION" ? "INCLUDED" : "DISABLED",
      provider_all:       selectAllProviders,
      doctor:             doctor.map((o) => o.label),
      doctor_mode:        doctorMode === "EXCLUSION" ? "EXCLUDED" : doctorMode === "INCLUSION" ? "INCLUDED" : "DISABLED",
      doctor_all:         selectAllDoctor,
      specialty:          specialty.map((o) => o.value),
      specialty_mode:     specialtyMode === "EXCLUSION" ? "EXCLUDED" : specialtyMode === "INCLUSION" ? "INCLUDED" : "DISABLED",
      specialty_all:      selectAllSpecialty,
      networks:           networks.map((o) => o.value),
      network_mode:       networkMode === "EXCLUSION" ? "EXCLUDED" : networkMode === "INCLUSION" ? "INCLUDED" : "DISABLED",
      network_all:        selectAllNetworks,
      insuranceplan:      insuranceplan.map((o) => o.value),
      insuranceplan_mode: insuranceMode === "EXCLUSION" ? "EXCLUDED" : insuranceMode === "INCLUSION" ? "INCLUDED" : "DISABLED",
      insuranceplan_all:  selectAllInsurance,
      payer:              Payer,
      payer_mode:         payerMode === "EXCLUSION" ? "EXCLUDED" : payerMode === "INCLUSION" ? "INCLUDED" : "DISABLED",
      payer_all:          selectAllPayer,
      patients:           patients.map((o) => o.label),
      patient_mode:       patientMode === "EXCLUSION" ? "EXCLUDED" : patientMode === "INCLUSION" ? "INCLUDED" : "DISABLED",
      patient_all:        selectAllPatients,
      policy:             buildPolicyValues(policy),
      policy_mode:        policyMode === "EXCLUSION" ? "EXCLUDED" : policyMode === "INCLUSION" ? "INCLUDED" : "DISABLED",
      policy_all:         selectAllPolicy,
      user_level:         userLevel,
      user_mode:          userMode === "EXCLUSION" ? "EXCLUDED" : userMode === "INCLUSION" ? "INCLUDED" : "DISABLED",
      user_all:           selectAllUser,
      vip:                vip.map((o) => o.value),
      vip_mode:           vipMode === "EXCLUSION" ? "EXCLUDED" : vipMode === "INCLUSION" ? "INCLUDED" : "DISABLED",
      vip_all:            selectAllVip,
      is_active:          isActive,
    };
  };

  // ── fetchItems — sorted, deleted last ──────────────────────────────────────

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Datum, { search: string; status: string; deleted: ServerDeletedFilterValue }>({
    filters: { search: filterSearch, status: filterStatus, deleted: deletedFilter },
    fetchPage: ({ page, limit, filters }) =>
      ActivityAllowedICDAxiosInstance.get(
        "getActivityAllowedIcd",
        {
          params: {
            page,
            limit,
            is_active: filters.status === "true" || filters.status === "false" ? filters.status : undefined,
            is_deleted: filters.deleted,
          },
        }
      ),
    setRows: (rows) => setItems({ data: rows }),
    normalizeRow: normalizeItem,
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    isClientFallbackActive: (filters) => filters.search.trim().length >= 2,
    clientFilterRows: (rows, filters) =>
      rows.filter((item) => rowMatchesUnifiedSearch(item, filters.search)),
    onError: (error) => {
      console.error("Failed to fetch Activity Must Come With Another ICD", error);
      toast({
        title: "Error",
        description: "Failed to fetch Activity Must Come With Another ICD",
        variant: "destructive",
      });
    },
  });

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  // ── Soft Delete ────────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityAllowedICDAxiosInstance.delete(`softDeleteActivityAllowedIcd/${id}`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Deleted successfully." });
      } else {
        setForceId(id); setForceAction("softDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ── Restore ────────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityAllowedICDAxiosInstance.patch(`restoreActivityAllowedIcd/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore.", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore.", variant: "destructive" });
    }
  };

  // ── Hard Delete ────────────────────────────────────────────────────────────
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteActivityAllowedIcd/${id}?force=true` : `deleteActivityAllowedIcd/${id}`;
      const response = await ActivityAllowedICDAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Permanently deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to permanently delete.");
    }
  };

  // ── Force Soft Delete ──────────────────────────────────────────────────────
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityAllowedICDAxiosInstance.delete(`softDeleteActivityAllowedIcd/${id}?force=true`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Force deleted successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete.");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete.");
    }
  };

  const toggleActiveStatus = async (item: Datum) => {
    const flat: CodeWithType[] = (item as any).activity_flat || [];
    try {
      const payload = {
        data: {
          relation_name:            item.relation_name,
          is_active:                !item.is_active,
          activity:                 flat.map((c) => c.code),
          activity_type:            flat.map((c) => c.type),
          types:                    Array.from(new Set(flat.map((c) => c.type))),
          activity_medicalgroups:   (item.activity_medicalgroups || []).map((g: any) => g.id ?? g),
          activity_customgroups:    (item.activity_customgroups  || []).map((g: any) => g.id ?? g),
          icd:                      item.icd || [],
          icd_medicalgroups:        (item.icd_medicalgroups || []).map((g: any) => g.id ?? g),
       icd_customgroups:         (item.icd_customgroups  || []).map((g: any) => g.id ?? g),
          group_validation_tick:    item.group_validation_tick ?? false,
          provider:                 (item.provider || []).map((o: any) => ({ Provider_id: o.Provider_id || "", Provider_name: o.Provider_name || "" })),
          apply_to_other_providers: item.apply_to_other_providers,
          select_all_providers:     item.select_all_providers ?? false,
          provider_mode:            item.apply_to_other_providers ? "exclude" : "include",
          doctor:                   (item.doctor || []).map((d: any) => d.Doctor_name ?? d),
          apply_to_other_doctor:    item.apply_to_other_doctor,
          select_all_doctor:        item.select_all_doctor ?? false,
          doctor_mode:              item.apply_to_other_doctor ? "exclude" : "include",
          specialty:                (item.specialty || []).map((s: any) => s.id ?? s),
          apply_to_other_specialty: item.apply_to_other_specialty,
          select_all_specialty:     item.select_all_specialty ?? false,
          specialty_mode:           item.apply_to_other_specialty ? "exclude" : "include",
          networks:                 (item.networks || []).map((n: any) => n.id ?? n),
          apply_to_other_networks:  item.apply_to_other_networks,
          select_all_networks:      item.select_all_networks ?? false,
          networks_mode:            item.apply_to_other_networks ? "exclude" : "include",
          insuranceplan:            (item.insuranceplan || []).map((i: any) => i.id ?? i),
          apply_to_other_insuranceplan: item.apply_to_other_insuranceplan,
          select_all_insuranceplan: item.select_all_insuranceplan ?? false,
          insuranceplan_mode:       item.apply_to_other_insuranceplan ? "exclude" : "include",
          payer:                    item.payer || [],
          apply_to_other_payer:     item.apply_to_other_payer,
          select_all_payer:         item.select_all_payer ?? false,
          payer_mode:               item.apply_to_other_payer ? "exclude" : "include",
          patients:                 (item.patients || []).map((p: any) => p.name ?? p),
          apply_to_other_patients:  item.apply_to_other_patients,
          select_all_patients:      item.select_all_patients ?? false,
          patients_mode:            item.apply_to_other_patients ? "exclude" : "include",
          policy:                   buildPolicyValues(item.policy || []),
          apply_to_other_policy:    item.apply_to_other_policy,
          select_all_policy:        item.select_all_policy ?? false,
          policy_mode:              item.apply_to_other_policy ? "exclude" : "include",
          user_level:               item.user_level || [],
          apply_to_other_user:      item.apply_to_other_user,
          select_all_user:          item.select_all_user ?? false,
          user_level_mode:          item.apply_to_other_user ? "exclude" : "include",
          vip:                      (item.vip || []).map((v: any) => v.id ?? v),
          apply_to_vip:             item.apply_to_vip,
          select_all_vip:           item.select_all_vip ?? false,
          vip_mode:                 item.apply_to_vip ? "exclude" : "include",
          id:                       item.id,
        },
        token,
      };
      const response = await ActivityAllowedICDAxiosInstance.put("updateActivityAllowedIcd", payload);
      if (response.data.success) {
        setItems((prev) => {
          const newData = [...prev.data];
          const idx = newData.findIndex((i) => i.id === item.id);
          if (idx !== -1) newData[idx] = { ...newData[idx], is_active: !item.is_active };
          return { data: newData };
        });
        toast({ title: "Success", description: `Status changed to ${!item.is_active ? "Active" : "Inactive"}.` });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const addItem = async (): Promise<void> => {
    if (!relationName.trim()) { toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return; }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    try {
      const response = await ActivityAllowedICDAxiosInstance.post("addnewActivityAllowedIcd", { data: [buildPayload()], token });
      if (response.data.success) {
        await fetchItems(); setCurrentPage(0); clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "Added successfully." });
      } else {
        const errors = response.data.errors || [];
        toast({ title: "Error", description: errors[0]?.error || response.data.message || "Failed to add.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!relationName.trim()) { toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return; }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    try {
      const response = await ActivityAllowedICDAxiosInstance.put("updateActivityAllowedIcd", { data: { ...buildPayload(), id }, token });
      if (response.data.success) {
        await fetchItems(); setCurrentPage(0); clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "Updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Datum) => {
        setClearIcd(false);
    setClearIcdMedicalGroups(false);
    setClearIcdCustomGroups(false);
    setClearActivityMedicalGroups(false);
    setClearActivityCustomGroups(false);
    setRelationName(item.relation_name);
    const actCodes: string[] = item.activity || [];
    const actTypes: string[] = Array.isArray(item.activity_type) ? item.activity_type : [];
    const flat: CodeWithType[] = actCodes.map((code, idx) => ({
      code,
      type: TYPE_MAP[(actTypes[idx] || "cpt").toLowerCase()] || "CPT",
    }));
    if (flat.length > 0) { setActivitiesByType(groupByType(flat)); } else { setActivitiesByType({ ...EMPTY_BY_TYPE }); }
    if (item.activity_medicalgroups?.length > 0) setActivityMedicalGroups(item.activity_medicalgroups.map((o: any) => typeof o === "string" ? { value: 0, label: o } : { value: o.id, label: o.name }));
    if (item.activity_customgroups?.length > 0)  setActivityCustomGroups(item.activity_customgroups.map((o: any) => typeof o === "string" ? { value: 0, label: o } : { value: o.id, label: o.name }));
    if (flat.length > 0)                          setSelectedMainOption("list_of_Activity_code");
    else if (item.activity_medicalgroups?.length > 0) setSelectedMainOption("medical_Activity_groups");
    else if (item.activity_customgroups?.length > 0)  setSelectedMainOption("custom_Activity_groups");
    else setSelectedMainOption(null);

    if (item.icd?.length > 0) { setIcd(item.icd.map((v: any) => ({ value: v, label: v.toString() }))); } else { setIcd([]); }
    if (item.icd_medicalgroups?.length > 0) setIcdMedicalGroups(item.icd_medicalgroups.map((o: any) => typeof o === "string" ? { value: 0, label: o } : { value: o.id, label: o.name }));
    if (item.icd_customgroups?.length > 0)  setIcdCustomGroups(item.icd_customgroups.map((o: any) => typeof o === "string" ? { value: 0, label: o } : { value: o.id, label: o.name }));
    setGroupValidationTick(item.group_validation_tick ?? false);
    if (item.icd?.length > 0)               setSelectedAllowedOption("list_of_icd");
    else if (item.icd_medicalgroups?.length > 0) setSelectedAllowedOption("medical_icd_groups");
    else if (item.icd_customgroups?.length > 0)  setSelectedAllowedOption("custom_icd_groups");
    else setSelectedAllowedOption(null);

    const toArr = (arr: any[], key: string) => (Array.isArray(arr) ? arr.map((o: any) => typeof o === "string" ? { id: 0, [key]: o } : o) : []);
    const mappedNetworks  = toArr(item.networks    || [], "network_name") .map((o: any) => ({ value: o.id,          label: o.network_name }));
    const mappedInsurance = toArr(item.insuranceplan || [], "Plan_name")  .map((o: any) => ({ value: o.id,          label: o.Plan_name }));
    const mappedProvider  = toArr(item.provider    || [], "Provider_name").map((o: any) => ({ value: o.Provider_id, label: o.Provider_name }));
    const mappedPatients  = (item.patients || []).map((o: any) =>
      typeof o === "object"
        ? { value: o.National_id ?? o.id ?? o.name, label: o.name }
        : { value: o, label: String(o) }
    );
    const mappedVip       = toArr(item.vip         || [], "name")         .map((o: any) => ({ value: o.id,          label: o.name }));
    const mappedDoctor    = toArr(item.doctor      || [], "Doctor_name")  .map((o: any) => ({ value: o.Doctor_id,   label: o.Doctor_name }));
    const mappedSpecialty = toArr(item.specialty   || [], "name")         .map((o: any) => ({ value: o.id,          label: o.name }));

    setNetworks(mappedNetworks); setInsuranceplan(mappedInsurance); setProvider(mappedProvider);
    setPatients(mappedPatients); setVip(mappedVip); setDoctor(mappedDoctor); setSpecialty(mappedSpecialty);
    setPolicy(Array.isArray(item.policy) ? item.policy.map((o: any) => ({ id: o.id || 0, Policy_id: o.Policy_id || "", Policy_name: o.Policy_name || "", plans: o.plans || [] })) : []);

    setProviderMode(modeFromString(item.provider_mode)); setIsProviderActive(item.provider_mode === "INCLUDED" || item.provider_mode === "EXCLUDED"); setSelectAllProviders(item.provider_all ?? false);
    setDoctorMode(modeFromString(item.doctor_mode)); setIsDoctorActive(item.doctor_mode === "INCLUDED" || item.doctor_mode === "EXCLUDED"); setSelectAllDoctor(item.doctor_all ?? false);
    setSpecialtyMode(modeFromString(item.specialty_mode)); setIsSpecialtyActive(item.specialty_mode === "INCLUDED" || item.specialty_mode === "EXCLUDED"); setSelectAllSpecialty(item.specialty_all ?? false);
    setNetworkMode(modeFromString(item.network_mode)); setIsNetworkActive(item.network_mode === "INCLUDED" || item.network_mode === "EXCLUDED"); setSelectAllNetworks(item.network_all ?? false);
    setInsuranceMode(modeFromString(item.insuranceplan_mode)); setIsInsuranceActive(item.insuranceplan_mode === "INCLUDED" || item.insuranceplan_mode === "EXCLUDED"); setSelectAllInsurance(item.insuranceplan_all ?? false);
    setPayerMode(modeFromString(item.payer_mode)); setIsPayerActive(item.payer_mode === "INCLUDED" || item.payer_mode === "EXCLUDED"); setPayer(item.payer || []); setSelectAllPayer(item.payer_all ?? false);
    setPolicyMode(modeFromString(item.policy_mode)); setIsPolicyActive(item.policy_mode === "INCLUDED" || item.policy_mode === "EXCLUDED"); setSelectAllPolicy(item.policy_all ?? false);
    setPatientMode(modeFromString(item.patient_mode)); setIsPatientActive(item.patient_mode === "INCLUDED" || item.patient_mode === "EXCLUDED"); setSelectAllPatients(item.patient_all ?? false);
    setVipMode(modeFromString(item.vip_mode)); setIsVipActive(item.vip_mode === "INCLUDED" || item.vip_mode === "EXCLUDED"); setSelectAllVip(item.vip_all ?? false);
    setUserMode(modeFromString(item.user_mode)); setIsUserActive(item.user_mode === "INCLUDED" || item.user_mode === "EXCLUDED"); setUserLevel(item.user_level || []); setSelectAllUser(item.user_all ?? false);

    const hasEntities = !!(
      mappedProvider.length  || item.select_all_providers    ||
      mappedDoctor.length    || item.select_all_doctor       ||
      mappedSpecialty.length || item.select_all_specialty    ||
      mappedNetworks.length  || item.select_all_networks     ||
      mappedInsurance.length || item.select_all_insuranceplan ||
      (item.policy || []).length || item.select_all_policy   ||
      mappedPatients.length  || item.select_all_patients     ||
      mappedVip.length       || item.select_all_vip          ||
      (item.payer || []).length  || item.select_all_payer    ||
      (item.user_level || []).length || item.select_all_user ||
      item.provider_mode === "INCLUDED" || item.provider_mode === "EXCLUDED" ||
      item.doctor_mode === "INCLUDED"   || item.doctor_mode === "EXCLUDED"   ||
      item.specialty_mode === "INCLUDED" || item.specialty_mode === "EXCLUDED" ||
      item.network_mode === "INCLUDED"  || item.network_mode === "EXCLUDED"  ||
      item.insuranceplan_mode === "INCLUDED" || item.insuranceplan_mode === "EXCLUDED" ||
      item.policy_mode === "INCLUDED"   || item.policy_mode === "EXCLUDED"   ||
      item.patient_mode === "INCLUDED"  || item.patient_mode === "EXCLUDED"  ||
      item.vip_mode === "INCLUDED"      || item.vip_mode === "EXCLUDED"      ||
      item.payer_mode === "INCLUDED"    || item.payer_mode === "EXCLUDED"    ||
      item.user_mode === "INCLUDED"     || item.user_mode === "EXCLUDED"
    );
    setShowEntities(hasEntities);
    setIsActive(item.is_active);
    setEditIndex(item.id);
  };

  const handleDownload = (item: Datum) => {
    const flat: CodeWithType[] = (item as any).activity_flat || [];
    const rows = ["relation_name;activity_code;activity_type;activity_medicalgroups;activity_customgroups;icd;icd_medicalgroups;icd_customgroups;provider;apply_to_other_providers;networks;apply_to_other_networks;insuranceplan;apply_to_other_insuranceplan;payer;apply_to_other_payer;patients;apply_to_other_patients;policy;apply_to_other_policy;user_level;apply_to_other_user;doctor;apply_to_other_doctor;vip;apply_to_vip;specialty;apply_to_other_specialty;is_active"];
    const icdArr = item.icd || [], medG = item.activity_medicalgroups || [], cusG = item.activity_customgroups || [];
    const medG2 = item.icd_medicalgroups || [], cusG2 = item.icd_customgroups || [];
    const prov = item.provider || [], nets = item.networks || [], ins = item.insuranceplan || [];
    const pats = item.patients || [], pol = item.policy || [], ul = item.user_level || [];
    const doc = item.doctor || [], pay = item.payer || [], v = item.vip || [];
    const spec = item.specialty || [];
    const maxLen = Math.max(flat.length, icdArr.length, medG.length, cusG.length, medG2.length, cusG2.length, prov.length, nets.length, ins.length, pats.length, pol.length, ul.length, doc.length, pay.length, v.length, spec.length, 1);
    for (let i = 0; i < maxLen; i++) {
      rows.push([
        item.relation_name || "",
        i < flat.length   ? flat[i].code : "", i < flat.length ? flat[i].type : "",
        i < medG.length   ? (medG[i]  as any).name || "" : "",
        i < cusG.length   ? (cusG[i]  as any).name || "" : "",
        i < icdArr.length ? icdArr[i] : "",
        i < medG2.length  ? (medG2[i] as any).name || "" : "",
        i < cusG2.length  ? (cusG2[i] as any).name || "" : "",
        i < prov.length   ? (prov[i]  as any).Provider_name || "" : "", item.apply_to_other_providers  ? "Exclude" : "Include",
        i < nets.length   ? (nets[i]  as any).network_name  || "" : "", item.apply_to_other_networks   ? "Exclude" : "Include",
        i < ins.length    ? (ins[i]   as any).Plan_name     || "" : "", item.apply_to_other_insuranceplan ? "Exclude" : "Include",
        i < pay.length    ? pay[i] : "",                                item.apply_to_other_payer      ? "Exclude" : "Include",
        i < pats.length   ? (pats[i]  as any).name          || "" : "", item.apply_to_other_patients   ? "Exclude" : "Include",
        i < pol.length    ? (pol[i]   as any).Policy_name   || "" : "", item.apply_to_other_policy     ? "Exclude" : "Include",
        i < ul.length     ? ul[i] : "",                                 item.apply_to_other_user       ? "Exclude" : "Include",
        i < doc.length    ? (doc[i]   as any).Doctor_name   || "" : "", item.apply_to_other_doctor     ? "Exclude" : "Include",
        i < v.length      ? (v[i]     as any).name          || "" : "", item.apply_to_vip              ? "Exclude" : "Include",
        i < spec.length   ? (spec[i]  as any).name          || "" : "", item.apply_to_other_specialty  ? "Exclude" : "Include",
        item.is_active ? "TRUE" : "FALSE",
      ].join(";"));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ActivityAllowedICD_${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
    cell: ({ row }) => (
  <div className="text-center w-full font-medium">
    {row.index + 1}
  </div>
),
    },
    {
      accessorKey: "relation_name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Relation Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = (row.original as any).is_deleted;
        return <div className={`w-full text-center ${isDeleted ? "line-through text-gray-400" : ""}`}>{row.getValue("relation_name") as string}</div>;
      },
    },
    {
      accessorKey: "activity",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Activity{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const flat: CodeWithType[] = (item as any).activity_flat || [];
        const custom  = Array.isArray(item.activity_customgroups)  ? item.activity_customgroups.map((g: any)  => g.name || g).filter(Boolean) : [];
        const medical = Array.isArray(item.activity_medicalgroups) ? item.activity_medicalgroups.map((g: any) => g.name || g).filter(Boolean) : [];
        if (flat.length === 0 && custom.length === 0 && medical.length === 0) return <div className="w-full text-center text-gray-400">—</div>;
        return (
          <ActivityCell
            items={flat} medicalGroups={medical} customGroups={custom}
            onShowAll={(its, med, cus) => {
              setAllCodesItems(its); setAllCodesMedical(med); setAllCodesCustom(cus);
              setAllCodesTitle(`${item.relation_name} – Activity Codes`);
              setAllCodesOpen(true);
            }}
          />
        );
      },
    },
    {
      accessorKey: "icd",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ICD{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const icdCodes  = Array.isArray(item.icd)               ? item.icd : [];
        const icdCustom = Array.isArray(item.icd_customgroups)  ? item.icd_customgroups.map((g: any)  => g.name || g) : [];
        const icdMedical= Array.isArray(item.icd_medicalgroups) ? item.icd_medicalgroups.map((g: any) => g.name || g) : [];
        const allDisplay = [
          ...icdCodes.map((c: string)   => ({ label: c, kind: "icd"     as const })),
          ...icdMedical.map((g: string) => ({ label: g, kind: "medical" as const })),
          ...icdCustom.map((g: string)  => ({ label: g, kind: "custom"  as const })),
        ];
        const total = allDisplay.length;
        if (total === 0) return <div className="w-full text-center text-gray-400">—</div>;
        const shown = allDisplay.slice(0, 3);
        const kindStyle = (kind: "icd" | "medical" | "custom") => {
          if (kind === "icd")     return { bg: "#011934", color: "#fff",    border: "#011934", tag: "ICD" };
          if (kind === "medical") return { bg: "#f0fdf4", color: "#15803d", border: "#86efac", tag: "MED" };
          return                         { bg: "#faf5ff", color: "#7e22ce", border: "#d8b4fe", tag: "CUS" };
        };
        return (
          <div className="w-full px-2 py-1.5">
            <div className="flex flex-wrap justify-center gap-1.5 mb-1">
              {shown.map((c, i) => {
                const s = kindStyle(c.kind);
                return (
                  <div key={i} style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"2px 4px 2px 8px", borderRadius:6, background:s.bg, border:`1px solid ${s.border}` }}>
                    <span style={{ fontSize:11, fontWeight:600, color:s.color }}>{c.label}</span>
                    <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:4, background:"rgba(255,255,255,0.2)", color:s.color, opacity:0.85, border:`1px solid ${s.border}` }}>{s.tag}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between border-t border-gray-100 pt-1">
              <span className="text-xs font-bold text-gray-500">{total}</span>
              {total > 3 && (
                <button onClick={() => { setIcdCodesDialogList(allDisplay); setIcdCodesDialogTitle(`${item.relation_name} – ICD Codes`); setIcdCodesDialogOpen(true); }} className="text-xs text-blue-600 hover:text-blue-800 font-semibold">
                  +{total - 3} more →
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active",
      enableSorting: false,
      header: () => <div className="w-full text-center normal-case font-normal">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => !isDeleted && toggleActiveStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => <div className="w-full text-center">View</div>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button onClick={() => setSelectedItem(row.original)} className="text-blue-600 hover:text-blue-800" title="View Details">
            <i className="fa-solid fa-eye text-lg" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-circle-check" />
          </span>
           Activity Must Come ICDs
        </h5>
      </div>

      {/* ══ VIEW DIALOG ══════════════════════════════════════════════════════ */}
      <Dialog open={Boolean(selectedItem)} onOpenChange={(open) => { if (!open) setSelectedItem(null); }}>
        <DialogContent className="max-w-[960px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow:"0 25px 60px rgba(0,0,0,0.18),0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (
            <>
              <div style={{ background:"linear-gradient(135deg,#0f172a 0%,#1e3a8a 60%,#2563eb 100%)", padding:"24px 28px 20px", flexShrink:0, position:"relative", overflow:"hidden" }}>
                <div style={{ position:"absolute", top:-40, right:-40, width:160, height:160, borderRadius:"50%", background:"rgba(255,255,255,0.04)" }} />
                <div style={{ position:"absolute", bottom:-30, left:-20, width:100, height:100, borderRadius:"50%", background:"rgba(255,255,255,0.03)" }} />
                <div style={{ display:"flex", alignItems:"flex-start", gap:16, position:"relative", zIndex:1 }}>
                  <div style={{ width:48, height:48, borderRadius:14, background:"rgba(255,255,255,0.12)", backdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, color:"#fff", flexShrink:0, border:"1px solid rgba(255,255,255,0.1)" }}>
                    <i className="fa-solid fa-circle-check" />
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:2 }}>
                      <h2 style={{ fontSize:20, fontWeight:700, color:"#fff", margin:0, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{selectedItem.relation_name}</h2>
                      <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 12px", borderRadius:9999, fontSize:11, fontWeight:600, flexShrink:0, background:selectedItem.is_active?"rgba(34,197,94,0.2)":"rgba(239,68,68,0.15)", color:selectedItem.is_active?"#86efac":"#fca5a5", border:`1px solid ${selectedItem.is_active?"rgba(34,197,94,0.25)":"rgba(239,68,68,0.2)"}` }}>
                        <span style={{ width:6, height:6, borderRadius:"50%", background:"currentColor" }} />
                        {selectedItem.is_active ? "Active" : "Inactive"}
                      </span>
                      {/* ── Deleted Badge ── */}
                      {(selectedItem as any).is_deleted && (
                        <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 12px", borderRadius:9999, fontSize:11, fontWeight:600, flexShrink:0, background:"rgba(239,68,68,0.2)", color:"#fca5a5", border:"1px solid rgba(239,68,68,0.3)" }}>
                          <i className="fa-solid fa-trash" style={{ fontSize:10 }} /> Deleted
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize:12, color:"rgba(147,197,253,0.8)", margin:0 }}>Rule ID: <span style={{ fontWeight:600, color:"#93c5fd" }}>#{selectedItem.id}</span></p>
                  </div>
                </div>
              </div>

              <div style={{ overflowY:"auto", flex:1, background:"#f8fafc", padding:"6px 0 16px" }}>
                <div style={{ padding:"20px 28px 16px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                    <div style={{ width:28, height:28, borderRadius:8, background:"#eff6ff", display:"flex", alignItems:"center", justifyContent:"center", border:"1px solid #dbeafe" }}>
                      <i className="fa-solid fa-code-branch" style={{ fontSize:12, color:"#3b82f6" }} />
                    </div>
                    <span style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>Activity & ICD Configuration</span>
                  </div>
                  {(() => {
                    const typeColors: Record<string,{bg:string;color:string;border:string;dot:string}> = {
                      CPT:   {bg:"#eff6ff",color:"#1d4ed8",border:"#bfdbfe",dot:"#3b82f6"},
                      HCPCS: {bg:"#f0fdf4",color:"#15803d",border:"#bbf7d0",dot:"#22c55e"},
                      Dental:{bg:"#faf5ff",color:"#7e22ce",border:"#e9d5ff",dot:"#a855f7"},
                      Drugs: {bg:"#fff7ed",color:"#c2410c",border:"#fed7aa",dot:"#f97316"},
                    };
                    const flat: CodeWithType[] = (selectedItem as any).activity_flat || [];
                    const grouped: Record<string,string[]> = {CPT:[],HCPCS:[],Dental:[],Drugs:[]};
                    flat.forEach(({code,type}) => { if (grouped[type]) grouped[type].push(String(code)); });
                    const medGroups    = selectedItem.activity_medicalgroups || [];
                    const customGroups = selectedItem.activity_customgroups  || [];
                    const icdCodes  = selectedItem.icd              || [];
                    const icdMed    = selectedItem.icd_medicalgroups || [];
                    const icdCustom = selectedItem.icd_customgroups  || [];
                    return (
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                        <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e2e8f0", padding:"16px 18px", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:14, paddingBottom:12, borderBottom:"1px solid #f1f5f9" }}>
                            <div style={{ width:24, height:24, borderRadius:6, background:"#eff6ff", display:"flex", alignItems:"center", justifyContent:"center" }}><i className="fa-solid fa-code" style={{ fontSize:10, color:"#3b82f6" }} /></div>
                            <span style={{ fontSize:12, fontWeight:700, color:"#1e293b" }}>Activity Codes</span>
                            <span style={{ marginLeft:"auto", fontSize:11, color:"#94a3b8" }}>{flat.length} codes</span>
                          </div>
                          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {flat.length > 0 && (
                              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                                {Object.entries(grouped).map(([type,codes]) => {
                                  if (!codes.length) return null;
                                  const tc = typeColors[type];
                                  return (
                                    <div key={type} style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", padding:"12px 14px" }}>
                                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                                        <span style={{ width:6, height:6, borderRadius:2, background:tc.dot, flexShrink:0 }} />
                                        <span style={{ fontSize:11, fontWeight:600, color:"#64748b" }}>{type}</span>
                                        <span style={{ fontSize:10, color:"#94a3b8", marginLeft:"auto" }}>{codes.length}</span>
                                      </div>
                                      <div style={{ display:"flex", flexWrap:"wrap" as const, gap:4 }}>
                                        {codes.map((code,idx) => <span key={idx} style={{ padding:"2px 8px", borderRadius:9999, fontSize:11, fontWeight:500, background:tc.bg, color:tc.color, border:`0.5px solid ${tc.border}` }}>{code}</span>)}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {[
                              {label:"Medical Groups", items:(medGroups as any[]).map(g=>g.name||g),    dot:"#22c55e", tagBg:"#f0fdf4", tagColor:"#15803d", tagBorder:"#bbf7d0"},
                              {label:"Custom Groups",  items:(customGroups as any[]).map(g=>g.name||g), dot:"#a855f7", tagBg:"#faf5ff", tagColor:"#7e22ce", tagBorder:"#e9d5ff"},
                            ].map(section => (
                              <div key={section.label} style={{ background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0", padding:"10px 12px" }}>
                                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                                  <span style={{ width:6, height:6, borderRadius:2, background:section.dot }} />
                                  <span style={{ fontSize:11, fontWeight:600, color:"#64748b" }}>{section.label}</span>
                                </div>
                                <div style={{ display:"flex", flexWrap:"wrap" as const, gap:5 }}>
                                  {section.items.length > 0
                                    ? section.items.map((it,i) => <span key={i} style={{ padding:"3px 10px", borderRadius:9999, fontSize:11, fontWeight:500, background:section.tagBg, color:section.tagColor, border:`0.5px solid ${section.tagBorder}` }}>{it}</span>)
                                    : <span style={{ color:"#94a3b8", fontSize:12, fontStyle:"italic" }}>None</span>}
                                </div>
                              </div>
                            ))}
                            {!flat.length && !(medGroups as any[]).length && !(customGroups as any[]).length && <span style={{ color:"#94a3b8", fontSize:12, fontStyle:"italic" }}>No activity data</span>}
                          </div>
                        </div>
                        <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e2e8f0", padding:"16px 18px", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:14, paddingBottom:12, borderBottom:"1px solid #f1f5f9" }}>
                            <div style={{ width:24, height:24, borderRadius:6, background:"#fff1f2", display:"flex", alignItems:"center", justifyContent:"center" }}><i className="fa-solid fa-stethoscope" style={{ fontSize:10, color:"#f43f5e" }} /></div>
                            <span style={{ fontSize:12, fontWeight:700, color:"#1e293b" }}>ICD Codes</span>
                            <span style={{ marginLeft:"auto", fontSize:11, color:"#94a3b8" }}>{icdCodes.length+(icdMed as any[]).length+(icdCustom as any[]).length} total</span>
                          </div>
                          <div style={{ marginBottom:10 }}>
                            <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", borderRadius:9999, fontSize:11, fontWeight:600, background:(selectedItem as any).group_validation_tick ? "#eff6ff" : "#f8fafc", color:(selectedItem as any).group_validation_tick ? "#1d4ed8" : "#64748b", border:`1px solid ${(selectedItem as any).group_validation_tick ? "#bfdbfe" : "#e2e8f0"}` }}>
                              <i className={`fa-solid ${(selectedItem as any).group_validation_tick ? "fa-link" : "fa-circle-dot"}`} style={{ fontSize:9 }} />
                              {(selectedItem as any).group_validation_tick ? "All ICDs Required Together" : "Any One ICD Required"}
                            </span>
                          </div>
                          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {icdCodes.length > 0 && (
                              <div style={{ background:"#fff1f2", borderRadius:10, border:"1px solid #fecdd3", padding:"10px 12px" }}>
                                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                                  <span style={{ width:6, height:6, borderRadius:2, background:"#f43f5e" }} />
                                  <span style={{ fontSize:11, fontWeight:600, color:"#64748b" }}>ICD Codes</span>
                                  <span style={{ fontSize:10, color:"#94a3b8", marginLeft:"auto" }}>{icdCodes.length}</span>
                                </div>
                                <div style={{ display:"flex", flexWrap:"wrap" as const, gap:5 }}>
                                  {icdCodes.map((c:any,i:number) => <span key={i} style={{ padding:"3px 10px", borderRadius:9999, fontSize:11, fontWeight:500, background:"#ffe4e6", color:"#be123c", border:"0.5px solid #fecdd3" }}>{c}</span>)}
                                </div>
                              </div>
                            )}
                            {[
                              {label:"Medical ICD Groups", items:(icdMed    as any[]).map(g=>g.name||g), dot:"#22c55e", tagBg:"#f0fdf4", tagColor:"#15803d", tagBorder:"#bbf7d0"},
                              {label:"Custom ICD Groups",  items:(icdCustom as any[]).map(g=>g.name||g), dot:"#a855f7", tagBg:"#faf5ff", tagColor:"#7e22ce", tagBorder:"#e9d5ff"},
                            ].map(section => (
                              <div key={section.label} style={{ background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0", padding:"10px 12px" }}>
                                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                                  <span style={{ width:6, height:6, borderRadius:2, background:section.dot }} />
                                  <span style={{ fontSize:11, fontWeight:600, color:"#64748b" }}>{section.label}</span>
                                </div>
                                <div style={{ display:"flex", flexWrap:"wrap" as const, gap:5 }}>
                                  {section.items.length > 0
                                    ? section.items.map((it,i) => <span key={i} style={{ padding:"3px 10px", borderRadius:9999, fontSize:11, fontWeight:500, background:section.tagBg, color:section.tagColor, border:`0.5px solid ${section.tagBorder}` }}>{it}</span>)
                                    : <span style={{ color:"#94a3b8", fontSize:12, fontStyle:"italic" }}>None</span>}
                                </div>
                              </div>
                            ))}
                            {!icdCodes.length && !(icdMed as any[]).length && !(icdCustom as any[]).length && <span style={{ color:"#94a3b8", fontSize:12, fontStyle:"italic" }}>No ICD data</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div style={{ height:1, background:"#e2e8f0", margin:"0 28px" }} />

                <div style={{ padding:"20px 28px 16px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                    <div style={{ width:28, height:28, borderRadius:8, background:"#fef3c7", display:"flex", alignItems:"center", justifyContent:"center", border:"1px solid #fde68a" }}>
                      <i className="fa-solid fa-bullseye" style={{ fontSize:12, color:"#d97706" }} />
                    </div>
                    <span style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>Target Scope & Visibility</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
{[
 {label:"Providers",       icon:"fa-hospital",         color:"#3b82f6", mode:selectedItem.provider_mode,       items:(selectedItem.provider||[]).map((p:any)=>p.Provider_id?`(${p.Provider_id}) ${p.Provider_name||""}`:p.Provider_name||String(p)), selectAll:(selectedItem as any).select_all_providers   ?? (selectedItem as any).provider_all},
{label:"Doctors",         icon:"fa-stethoscope",      color:"#06b6d4", mode:selectedItem.doctor_mode,         items:(selectedItem.doctor||[]).map((d:any)=>d.Doctor_id?`(${d.Doctor_id}) ${d.Doctor_name||""}`:d.Doctor_name||String(d)),           selectAll:(selectedItem as any).select_all_doctor      ?? (selectedItem as any).doctor_all},
{label:"Specialty",       icon:"fa-user-doctor",      color:"#0ea5e9", mode:selectedItem.specialty_mode,      items:(selectedItem.specialty||[]).map((s:any)=>s.id?`(${s.id}) ${s.name||""}`:s.name||String(s)),                                   selectAll:(selectedItem as any).select_all_specialty   ?? (selectedItem as any).specialty_all},
{label:"Networks",        icon:"fa-network-wired",    color:"#8b5cf6", mode:selectedItem.network_mode,        items:(selectedItem.networks||[]).map((n:any)=>n.id?`(${n.id}) ${n.network_name||""}`:n.network_name||String(n)),                     selectAll:(selectedItem as any).select_all_networks    ?? (selectedItem as any).network_all},
{label:"Insurance Plans", icon:"fa-shield-halved",    color:"#f59e0b", mode:selectedItem.insuranceplan_mode,  items:(selectedItem.insuranceplan||[]).map((i:any)=>i.id?`(${i.id}) ${i.Plan_name||""}`:i.Plan_name||String(i)),                       selectAll:(selectedItem as any).select_all_insuranceplan ?? (selectedItem as any).insuranceplan_all},
{label:"Policies",        icon:"fa-file-lines",       color:"#10b981", mode:selectedItem.policy_mode,         items:(selectedItem.policy||[]).map((p:any)=>{const plans=(p.plans||[]).map((pl:any)=>pl.Plan_name||pl.plan_name||pl.name).filter(Boolean);return `(${p.Policy_id||""}) ${p.Policy_name||""}${plans.length?` (${plans.join(", ")})`:""}`;}), selectAll:(selectedItem as any).select_all_policy      ?? (selectedItem as any).policy_all},
{label:"Patients",        icon:"fa-users",            color:"#ec4899", mode:selectedItem.patient_mode,        items:(selectedItem.patients||[]).map((p:any)=>p.National_id?`(${p.National_id}) ${p.name||""}`:p.name||String(p)),                   selectAll:(selectedItem as any).select_all_patients    ?? (selectedItem as any).patient_all},
{label:"VIP",             icon:"fa-star",             color:"#eab308", mode:selectedItem.vip_mode,            items:(selectedItem.vip||[]).map((v:any)=>v.id?`(${v.id}) ${v.name||""}`:v.name||String(v)),                                         selectAll:(selectedItem as any).select_all_vip         ?? (selectedItem as any).vip_all},
{label:"Payer",           icon:"fa-building-columns", color:"#6366f1", mode:selectedItem.payer_mode,          items:(selectedItem.payer||[]).map((p:any)=>String(p)),                                                                               selectAll:(selectedItem as any).select_all_payer       ?? (selectedItem as any).payer_all},
{label:"User Levels",     icon:"fa-user-gear",        color:"#64748b", mode:selectedItem.user_mode,           items:(selectedItem.user_level||[]).map((u:any)=>String(u)),                                                                          selectAll:(selectedItem as any).select_all_user        ?? (selectedItem as any).user_all},
].map(e => (
  <div key={e.label} style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", padding:"14px 16px", boxShadow:"0 1px 3px rgba(0,0,0,0.03)", display:"flex", flexDirection:"column" as const }}>
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
      <div style={{ display:"flex", alignItems:"center", gap:7 }}>
        <div style={{ width:24, height:24, borderRadius:6, background:`${e.color}12`, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <i className={`fa-solid ${e.icon}`} style={{ fontSize:10, color:e.color }} />
        </div>
        <span style={{ fontSize:12, fontWeight:600, color:"#334155" }}>{e.label}</span>
      </div>
     {e.mode && e.mode !== "DISABLED" && (
  <span style={{ fontSize:10, padding:"2px 8px", borderRadius:9999, fontWeight:600, background:e.mode==="EXCLUDED"?"#fef2f2":"#f0fdf4", color:e.mode==="EXCLUDED"?"#dc2626":"#15803d", border:`1px solid ${e.mode==="EXCLUDED"?"#fecaca":"#bbf7d0"}` }}>
    {e.mode==="EXCLUDED" ? "Exclusion" : "Inclusion"}
  </span>
)}
    </div>
{e.selectAll
  ? <span style={{ fontSize:11, padding:"3px 10px", borderRadius:9999, background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe", fontWeight:600, display:"inline-flex", alignItems:"center", gap:4, width:"fit-content" }}>
      <i className="fa-solid fa-check-double" style={{ fontSize:9 }} /> All Selected
    </span>
  : (e.mode && e.mode !== "DISABLED")
    ? e.items.length > 0
      ? <div style={{ display:"flex", flexWrap:"wrap" as const, gap:5 }}>
          {e.items.map((it:string, i:number) => (
            <span key={i} style={{ fontSize:11, padding:"3px 10px", borderRadius:9999, background:"#f1f5f9", color:"#334155", border:"1px solid #e2e8f0", fontWeight:500 }}>{it}</span>
          ))}
        </div>
      : <span style={{ color:"#cbd5e1", fontSize:12, fontStyle:"italic" }}>No items</span>
    : <span style={{ color:"#cbd5e1", fontSize:12, fontStyle:"italic" }}>—</span>
}
  </div>
))}
                  </div>
                </div>

                <div style={{ height:1, background:"#e2e8f0", margin:"0 28px" }} />

                <div style={{ padding:"20px 28px 8px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                    <div style={{ width:28, height:28, borderRadius:8, background:"#f0fdf4", display:"flex", alignItems:"center", justifyContent:"center", border:"1px solid #bbf7d0" }}>
                      <i className="fa-solid fa-clock-rotate-left" style={{ fontSize:12, color:"#16a34a" }} />
                    </div>
                    <span style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>Audit Trail</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                    <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", padding:"14px 16px" }}>
                      <span style={{ fontSize:10, fontWeight:600, color:"#94a3b8" }}>Created By</span>
                      <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:10 }}>
                        <div style={{ width:32, height:32, borderRadius:"50%", background:"linear-gradient(135deg,#3b82f6,#2563eb)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:"#fff", flexShrink:0 }}>
                          {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize:13, fontWeight:600, color:"#1e293b" }}>{(selectedItem as any).created_by!=null ? resolveUserName((selectedItem as any).created_by) : "—"}</span>
                      </div>
                    </div>
                    <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", padding:"14px 16px" }}>
                      <span style={{ fontSize:10, fontWeight:600, color:"#94a3b8" }}>Created On</span>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:10 }}>
                        <i className="fa-regular fa-calendar" style={{ fontSize:14, color:"#3b82f6" }} />
                        <span style={{ fontSize:13, fontWeight:600, color:"#1e293b" }}>{(selectedItem as any).createdat ? new Date((selectedItem as any).createdat).toLocaleString("en-GB",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "—"}</span>
                      </div>
                    </div>
                    <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", padding:"14px 16px" }}>
                      <span style={{ fontSize:10, fontWeight:600, color:"#94a3b8" }}>Last Updated</span>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:10 }}>
                        <i className="fa-regular fa-clock" style={{ fontSize:14, color:"#10b981" }} />
                        <span style={{ fontSize:13, fontWeight:600, color:"#1e293b" }}>{(selectedItem as any).updatedat ? new Date((selectedItem as any).updatedat).toLocaleString("en-GB",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "—"}</span>
                      </div>
                    </div>
                  </div>
         {Array.isArray(selectedItem.updated_by) && selectedItem.updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {selectedItem.updated_by.length} update{selectedItem.updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {(selectedItem.updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
            {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
            {entry.at ? new Date(entry.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
          </span>
        </div>
      ))}
      {selectedItem.updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
            {showAllUpdates ? "Show less ↑" : `+${selectedItem.updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                </div>
              </div>

              <div style={{ padding:"14px 28px", borderTop:"1px solid #e2e8f0", background:"#fff", display:"flex", alignItems:"center", justifyContent:"flex-end", gap:10, flexShrink:0 }}>
                <Button onClick={() => setSelectedItem(null)} style={{ background:"linear-gradient(135deg,#1e3a8a,#2563eb)", color:"#fff", fontWeight:600, padding:"8px 28px", borderRadius:10 }}>
                  Close Details
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ══ ADD / EDIT DIALOG ════════════════════════════════════════════════ */}
    {/* ══ ADD / EDIT DIALOG ════════════════════════════════════════════════ */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-6xl h-[90vh] flex flex-col overflow-hidden p-0">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add  Activity Must Come ICDs" : "Edit  Activity Must Come ICDs"}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Fill in the details below to {editIndex === null ? "create" : "update"} the record.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
            {/* Section 1 */}
            <SectionCard num={1} title="Basic information" subtitle="Name and status of the relation">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Label htmlFor="relation_name">Relation Name <span className="text-red-500 ml-0.5">*</span></Label>
                  <Input id="relation_name" value={relationName} onChange={(e) => setRelationName(e.target.value)} placeholder="Enter Relation Name" className="mt-1" />
                </div>
                <div className="w-48 flex-shrink-0">
                  <Label>Status</Label>
                  <div className="flex gap-2 mt-1">
                    <button type="button" onClick={() => setIsActive(true)} className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${isActive ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Active</button>
                    <button type="button" onClick={() => setIsActive(false)} className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${!isActive ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Inactive</button>
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Section 2 */}
            <SectionCard num={2} title="Activity & ICD configuration" subtitle="Choose one source for each side">
              <div className="space-y-5">
                <SourcePicker
                  title="Activity"
                  selected={selectedMainOption}
                  onSelect={setSelectedMainOption}
                  options={[
                    { key: "list_of_Activity_code", label: "Activity Codes", icon: "fa-code", count: activitiesByType.CPT.length + activitiesByType.HCPCS.length + activitiesByType.Dental.length + activitiesByType.Drugs.length },
                    { key: "medical_Activity_groups", label: "Medical Groups", icon: "fa-layer-group", count: ActivityMedicalGroups.length },
                    { key: "custom_Activity_groups", label: "Custom Groups", icon: "fa-cubes", count: ActivityCustomGroups.length },
                  ]}
                >
                  {selectedMainOption === "list_of_Activity_code" && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><i className="fa-solid fa-code text-blue-600" /> Activity Codes by Type</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {(["CPT", "HCPCS", "Dental", "Drugs"] as const).map((key) => (
                          <div key={key} className="bg-white p-3 rounded-lg border border-gray-200">
                            <Label className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-bold border">{key}</span>
                              <span className="text-xs text-gray-500">({activitiesByType[key].length} codes)</span>
                            </Label>
                            <ListofActivityCode onChange={(selected: Option[]) => setActivitiesByType(prev => ({ ...prev, [key]: selected }))} type={key} defaultValue={activitiesByType[key]} clear={false} isValid={true} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedMainOption === "medical_Activity_groups" && (
                    <ListOfActivityMedical onChange={setActivityMedicalGroups} defaultValue={ActivityMedicalGroups} clear={clearActivityMedicalGroups} />
                  )}
                  {selectedMainOption === "custom_Activity_groups" && (
                    <ListOfActivityCustom onChange={setActivityCustomGroups} defaultValue={ActivityCustomGroups} clear={clearActivityCustomGroups} />
                  )}
                </SourcePicker>

                <SourcePicker
                  title="ICD"
                  selected={selectedAllowedOption}
                  onSelect={setSelectedAllowedOption}
                  options={[
                    { key: "list_of_icd", label: "List of ICD", icon: "fa-file-medical", count: icd.length },
                    { key: "medical_icd_groups", label: "Medical Groups", icon: "fa-layer-group", count: icdMedicalGroups.length },
                    { key: "custom_icd_groups", label: "Custom Groups", icon: "fa-cubes", count: icdCustomGroups.length },
                  ]}
                >
                  {selectedAllowedOption === "list_of_icd" && (
                    <Listofmedicalcode onChange={setIcd} description={IcdDescription} defaultValue={icd} clear={clearIcd} />
                  )}
                  {selectedAllowedOption === "medical_icd_groups" && (
                    <ListofMedicalGroup onChange={setIcdMedicalGroups} defaultValue={icdMedicalGroups} clear={clearIcdMedicalGroups} />
                  )}
                  {selectedAllowedOption === "custom_icd_groups" && (
                    <ListofCustomIcd onChange={setIcdCustomGroups} defaultValue={icdCustomGroups} clear={clearIcdCustomGroups} />
                  )}
                </SourcePicker>

                <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 w-fit">
                  <input
                    type="checkbox"
                    checked={groupValidationTick}
                    onChange={(e) => setGroupValidationTick(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 accent-blue-600"
                  />
                  <span className="text-sm font-medium text-slate-600">
                    All ICDs must come together (Ticks checking)
                  </span>
                </label>
              </div>
            </SectionCard>

            {/* Section 3 – Entities */}
            <div className="border-2 border-blue-200 rounded-xl overflow-hidden mb-4 shadow-sm">
              <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">3</span>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Entities Configuration</p>
                    <p className="text-xs text-blue-600">Scope and visibility — who this rule applies to</p>
                  </div>
                </div>
                <button type="button" onClick={handleToggleEntities} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showEntities ? "bg-green-500" : "bg-gray-300"}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showEntities ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
              {showEntities && (
                <div className="p-5 bg-blue-50">
                  <div className="flex gap-3 p-3 bg-blue-100 border-l-4 border-blue-500 rounded-r-lg mb-4">
                    <svg className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    <p className="text-xs text-blue-900 leading-relaxed">
                      <span className="font-semibold text-emerald-700">Inclusion</span> = apply only to selected items &nbsp;·&nbsp;
                      <span className="font-semibold text-red-700">Exclusion</span> = apply to everyone except selected items
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <EntityCard label="Providers" icon="fa-hospital" color="blue" isActive={isProviderActive} mode={providerMode}
                      onExclusion={() => { if (providerMode==="EXCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("EXCLUSION"); setIsProviderActive(true); } }}
                      onInclusion={() => { if (providerMode==="INCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("INCLUSION"); setIsProviderActive(true); } }}
                      onClear={() => { setProvider([]); setProviderMode(null); setIsProviderActive(false); setClearProvider(true); setSelectAllProviders(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllProviders} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllProviders(c); if (c) { setProvider([]); setClearProvider(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-blue-600" /><span className="text-sm font-medium text-slate-600">Select All Providers</span></label>
                      {!selectAllProviders && <ListOfProviderName onChange={setProvider} defaultValue={provider} clear={clearProvider} />}
                    </EntityCard>

                    <EntityCard label="Doctor" icon="fa-stethoscope" color="cyan" isActive={isDoctorActive} mode={doctorMode}
                      onExclusion={() => { if (doctorMode==="EXCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("EXCLUSION"); setIsDoctorActive(true); } }}
                      onInclusion={() => { if (doctorMode==="INCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("INCLUSION"); setIsDoctorActive(true); } }}
                      onClear={() => { setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setClearDoctor(true); setSelectAllDoctor(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllDoctor} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllDoctor(c); if (c) { setDoctor([]); setClearDoctor(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-cyan-600" /><span className="text-sm font-medium text-slate-600">Select All Doctors</span></label>
                      {!selectAllDoctor && <ListOfDoctorName onChange={setDoctor} defaultValue={doctor} clear={clearDoctor} />}
                    </EntityCard>

                    <EntityCard label="Specialty" icon="fa-user-doctor" color="teal" isActive={isSpecialtyActive} mode={specialtyMode}
                      onExclusion={() => { if (specialtyMode==="EXCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("EXCLUSION"); setIsSpecialtyActive(true); } }}
                      onInclusion={() => { if (specialtyMode==="INCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("INCLUSION"); setIsSpecialtyActive(true); } }}
                      onClear={() => { setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setClearSpecialty(true); setSelectAllSpecialty(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllSpecialty} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllSpecialty(c); if (c) { setSpecialty([]); setClearSpecialty(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-teal-600" /><span className="text-sm font-medium text-slate-600">Select All Specialties</span></label>
                      {!selectAllSpecialty && <ListOfSpecialtyName onChange={setSpecialty} defaultValue={specialty} clear={clearSpecialty} />}
                    </EntityCard>

                    <EntityCard label="Networks" icon="fa-network-wired" color="purple" isActive={isNetworkActive} mode={networkMode}
                      onExclusion={() => { if (networkMode==="EXCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("EXCLUSION"); setIsNetworkActive(true); } }}
                      onInclusion={() => { if (networkMode==="INCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("INCLUSION"); setIsNetworkActive(true); } }}
                      onClear={() => { setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setClearNetworks(true); setSelectAllNetworks(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllNetworks} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllNetworks(c); if (c) { setNetworks([]); setClearNetworks(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-purple-600" /><span className="text-sm font-medium text-slate-600">Select All Networks</span></label>
                      {!selectAllNetworks && <ListOfNewNetworkName onChange={setNetworks} defaultValue={networks} clear={clearNetworks} />}
                    </EntityCard>

                    <EntityCard label="Insurance Plan" icon="fa-shield-halved" color="amber" isActive={isInsuranceActive} mode={insuranceMode}
                      onExclusion={() => { if (insuranceMode==="EXCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("EXCLUSION"); setIsInsuranceActive(true); } }}
                      onInclusion={() => { if (insuranceMode==="INCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("INCLUSION"); setIsInsuranceActive(true); } }}
                      onClear={() => { setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setClearInsuranceplan(true); setSelectAllInsurance(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllInsurance} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllInsurance(c); if (c) { setInsuranceplan([]); setClearInsuranceplan(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-amber-600" /><span className="text-sm font-medium text-slate-600">Select All Insurance Plans</span></label>
                      {!selectAllInsurance && <ListOfInsuranceplanName onChange={setInsuranceplan} defaultValue={insuranceplan} clear={clearInsuranceplan} />}
                    </EntityCard>

                    <EntityCard label="Policy" icon="fa-file-lines" color="emerald" isActive={isPolicyActive} mode={policyMode}
                      onExclusion={() => { if (policyMode==="EXCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("EXCLUSION"); setIsPolicyActive(true); } }}
                      onInclusion={() => { if (policyMode==="INCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("INCLUSION"); setIsPolicyActive(true); } }}
                      onClear={() => { setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setClearPolicy(true); setSelectAllPolicy(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllPolicy} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPolicy(c); if (c) { setPolicy([]); setClearPolicy(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-emerald-600" /><span className="text-sm font-medium text-slate-600">Select All Policies</span></label>
                      {!selectAllPolicy && <PolicyNames onChange={(v: PolicyItem[]) => setPolicy(v)} defaultValue={policy} clear={clearPolicy} />}
                    </EntityCard>

                    <EntityCard label="Patients" icon="fa-users" color="pink" isActive={isPatientActive} mode={patientMode}
                      onExclusion={() => { if (patientMode==="EXCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("EXCLUSION"); setIsPatientActive(true); } }}
                      onInclusion={() => { if (patientMode==="INCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("INCLUSION"); setIsPatientActive(true); } }}
                      onClear={() => { setPatients([]); setPatientMode(null); setIsPatientActive(false); setClearPatient(true); setSelectAllPatients(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllPatients} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPatients(c); if (c) { setPatients([]); setClearPatient(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-pink-600" /><span className="text-sm font-medium text-slate-600">Select All Patients</span></label>
                      {!selectAllPatients && <VipPatientSelector onChange={setPatients} defaultValue={patients} clear={clearPatient} />}
                    </EntityCard>

                    <EntityCard label="VIP" icon="fa-star" color="yellow" isActive={isVipActive} mode={vipMode}
                      onExclusion={() => { if (vipMode==="EXCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("EXCLUSION"); setIsVipActive(true); } }}
                      onInclusion={() => { if (vipMode==="INCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("INCLUSION"); setIsVipActive(true); } }}
                      onClear={() => { setVip([]); setVipMode(null); setIsVipActive(false); setClearVip(true); setSelectAllVip(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllVip} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllVip(c); if (c) { setVip([]); setClearVip(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-yellow-600" /><span className="text-sm font-medium text-slate-600">Select All VIP</span></label>
                      {!selectAllVip && <Vip onChange={setVip} defaultValue={vip} clear={clearVip} />}
                    </EntityCard>

                    <EntityCard label="Payer" icon="fa-building-columns" color="indigo" isActive={isPayerActive} mode={payerMode}
                      onExclusion={() => { if (payerMode==="EXCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("EXCLUSION"); setIsPayerActive(true); } }}
                      onInclusion={() => { if (payerMode==="INCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("INCLUSION"); setIsPayerActive(true); } }}
                      onClear={() => { setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllPayer} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPayer(c); if (c) setPayer([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-indigo-600" /><span className="text-sm font-medium text-slate-600">Select All Payers</span></label>
                      {!selectAllPayer && <Input value={Payer.join(",")} onChange={(e) => setPayer([e.target.value])} placeholder="Enter Payer" />}
                    </EntityCard>

                    <EntityCard label="User Level" icon="fa-user-gear" color="slate" isActive={isUserActive} mode={userMode}
                      onExclusion={() => { if (userMode==="EXCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("EXCLUSION"); setIsUserActive(true); } }}
                      onInclusion={() => { if (userMode==="INCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("INCLUSION"); setIsUserActive(true); } }}
                      onClear={() => { setUserLevel([]); setUserMode(null); setIsUserActive(false); setSelectAllUser(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllUser} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllUser(c); if (c) setUserLevel([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-slate-600" /><span className="text-sm font-medium text-slate-600">Select All User Levels</span></label>
                      {!selectAllUser && <Input value={userLevel.join(",")} onChange={(e) => setUserLevel([e.target.value])} placeholder="Enter User Level" />}
                    </EntityCard>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}>Cancel</Button>
            <Button onClick={() => editIndex === null ? addItem() : updateItem(editIndex)} className="bg-blue-700 hover:bg-blue-800 text-white">
              {editIndex === null ? "Add Relation " : "Update Relation "}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══ ALL CODES POPUP ══════════════════════════════════════════════════ */}
      <Dialog open={allCodesOpen} onOpenChange={setAllCodesOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-blue-900 flex items-center gap-2">
              <i className="fa-solid fa-list" /> {allCodesTitle}
              <span className="ml-auto text-sm font-normal text-gray-500">({allCodesItems.length+allCodesMedical.length+allCodesCustom.length} total)</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-3 overflow-y-auto max-h-[calc(80vh-120px)] space-y-4">
            {allCodesItems.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-gray-500 mb-2">Activity Codes ({allCodesItems.length})</h4>
                <div className="grid grid-cols-4 gap-2">
                  {allCodesItems.map((item, idx) => { const col = getActivityTypeColor(item.type); return (<div key={idx} className={`flex items-center justify-between gap-2 p-2.5 bg-white rounded-lg border ${col.border}`}><span className={`font-bold text-sm ${col.text}`}>{item.code}</span><span className={`text-[10px] font-bold uppercase ${col.text} opacity-70`}>{item.type}</span></div>); })}
                </div>
              </div>
            )}
            {allCodesMedical.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-teal-600 mb-2">Medical Groups ({allCodesMedical.length})</h4>
                <div className="grid grid-cols-3 gap-2">
                  {allCodesMedical.map((g,idx) => (<div key={idx} className="flex items-center justify-between gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-400"><span className="font-bold text-sm text-slate-700">{g}</span><span className="text-xs font-bold text-slate-500 uppercase">Medical</span></div>))}
                </div>
              </div>
            )}
            {allCodesCustom.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-blue-500 mb-2">Custom Groups ({allCodesCustom.length})</h4>
                <div className="grid grid-cols-3 gap-2">
                  {allCodesCustom.map((g,idx) => (<div key={idx} className="flex items-center justify-between gap-2 p-2.5 bg-blue-50 rounded-lg border border-blue-300"><span className="font-bold text-sm text-blue-800">{g}</span><span className="text-xs font-bold text-blue-500 uppercase">Custom</span></div>))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3 border-t mt-3">
            <Button onClick={() => setAllCodesOpen(false)} className="bg-blue-700 hover:bg-blue-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══ ICD CODES DIALOG ═════════════════════════════════════════════════ */}
      <Dialog open={icdCodesDialogOpen} onOpenChange={setIcdCodesDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fa-solid fa-stethoscope text-rose-500" />
              {icdCodesDialogTitle}
              <span className="ml-2 text-sm font-normal text-gray-400">({icdCodesDialogList.length} codes)</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-3 overflow-y-auto max-h-[calc(80vh-120px)] space-y-4">
            {icdCodesDialogList.filter(x=>x.kind==="icd").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2"><span style={{ width:8, height:8, borderRadius:2, background:"#011934", display:"inline-block" }} /><span className="text-xs font-bold text-gray-600 tracking-wide">ICD Codes ({icdCodesDialogList.filter(x=>x.kind==="icd").length})</span></div>
                <div className="flex flex-wrap gap-2">{icdCodesDialogList.filter(x=>x.kind==="icd").map((c,i) => <span key={i} style={{ padding:"4px 12px", background:"#011934", color:"white", borderRadius:6, fontSize:12, fontWeight:600 }}>{c.label}</span>)}</div>
              </div>
            )}
            {icdCodesDialogList.filter(x=>x.kind==="medical").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2"><span style={{ width:8, height:8, borderRadius:2, background:"#16a34a", display:"inline-block" }} /><span className="text-xs font-bold text-green-700 tracking-wide">Medical ICD Groups ({icdCodesDialogList.filter(x=>x.kind==="medical").length})</span></div>
                <div className="flex flex-wrap gap-2">{icdCodesDialogList.filter(x=>x.kind==="medical").map((c,i) => <span key={i} style={{ padding:"4px 12px", background:"#f0fdf4", color:"#15803d", borderRadius:6, fontSize:12, fontWeight:600, border:"1px solid #86efac" }}>{c.label}</span>)}</div>
              </div>
            )}
            {icdCodesDialogList.filter(x=>x.kind==="custom").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2"><span style={{ width:8, height:8, borderRadius:2, background:"#9333ea", display:"inline-block" }} /><span className="text-xs font-bold text-purple-700 tracking-wide">Custom ICD Groups ({icdCodesDialogList.filter(x=>x.kind==="custom").length})</span></div>
                <div className="flex flex-wrap gap-2">{icdCodesDialogList.filter(x=>x.kind==="custom").map((c,i) => <span key={i} style={{ padding:"4px 12px", background:"#faf5ff", color:"#7e22ce", borderRadius:6, fontSize:12, fontWeight:600, border:"1px solid #d8b4fe" }}>{c.label}</span>)}</div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3 border-t mt-3">
            <Button onClick={() => setIcdCodesDialogOpen(false)} className="bg-blue-700 hover:bg-blue-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══ DataTable ════════════════════════════════════════════════════════ */}
      <DataTable2
        columns={columns}
        data={items.data}
        searchColumns={[]}
        validateButtonLabel="Validate"
        addButtonLabel="Add New Relation"
        showAddButton={false}
        onEdit={handleEdit}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onDelete={(id) => {
          const item = items.data.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id); setForceAction("hardDelete"); setForceError(""); setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
        onAdd={() => { setEditIndex(null); clear(); setIsDialogOpen(true); }}
        onEditClick={(item) => { handleEdit(item); setIsDialogOpen(true); }}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ width:"100%", position:"relative" }}>
<div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", padding:"10px 160px 10px 16px", background:"linear-gradient(135deg,#f8fafc 0%,#f1f5f9 100%)", borderRadius:12, border:"1px solid #e2e8f0", boxShadow:"0 1px 3px rgba(0,0,0,0.04)", marginBottom:12 }}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
              <button type="button" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(true); }} style={{ display:"flex", alignItems:"center", gap:7, padding:"8px 18px", fontSize:13, fontWeight:600, color:"#ffffff", background:"#1e3a8a", border:"none", borderRadius:8, cursor:"pointer", whiteSpace:"nowrap", boxShadow:"0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                Add New Relation
              </button>
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Relation Name, Activity, ICD, Medical Group, or Custom Group."
              />
              <div style={{ width:1, height:28, background:"#cbd5e1" }} />
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:11, fontWeight:600, color:"#64748b", letterSpacing:"0.05em" }}>Status</span>
                <div style={{ display:"flex", alignItems:"center", padding:4, background:"#e2e8f0", borderRadius:9999 }}>
                  {[{value:"",label:"All"},{value:"true",label:"Active"},{value:"false",label:"Inactive"}].map(opt => (
                    <button key={opt.value} type="button" onClick={() => setFilterStatus(opt.value)} style={{ padding:"6px 14px", fontSize:13, fontWeight:500, borderRadius:9999, border:"none", cursor:"pointer", background:filterStatus===opt.value ? opt.value==="true" ? "#10b981" : opt.value==="false" ? "#94a3b8" : "#ffffff" : "transparent", color:filterStatus===opt.value ? opt.value==="" ? "#334155" : "#ffffff" : "#64748b", boxShadow:filterStatus===opt.value?"0 2px 4px rgba(0,0,0,0.1)":"none" }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ position:"absolute", top:0, right:16, height:62, display:"flex", alignItems:"center" }}>
              <FileUpload fetchItems={fetchItems} />
            </div>
          </div>
        }
      />

      {/* ══ FORCE / HARD DELETE DIALOG ═══════════════════════════════════════ */}
      {forceDialogOpen && (
        <div style={{ position:"fixed", inset:0, background:"#00000088", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(4px)" }}>
          <div style={{ background:"#fff", borderRadius:16, padding:32, width:440, boxShadow:"0 20px 50px rgba(0,0,0,0.3)", border:"1px solid #fecaca" }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", gap:12 }}>
              <div style={{ width:60, height:60, borderRadius:"50%", background:"#fef2f2", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize:28, color:"#dc2626" }}></i>
              </div>

              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize:18, fontWeight:700, color:"#1e293b", margin:0 }}>Permanent Delete</h3>
                  <p style={{ fontSize:13, color:"#64748b", margin:0, lineHeight:1.6 }}>This will <strong style={{ color:"#dc2626" }}>permanently delete</strong> this record.<br />This action <strong>cannot be undone</strong>.</p>
                  {forceError && (
                    <div style={{ width:"100%", background:"#fef9c3", border:"1px solid #fde047", borderRadius:10, padding:"12px 16px", textAlign:"left" }}>
                      <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color:"#ca8a04", marginTop:2, flexShrink:0 }}></i>
                        <p style={{ fontSize:13, color:"#713f12", margin:0, lineHeight:1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                  <div style={{ display:"flex", gap:10, width:"100%", marginTop:4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }} style={{ flex:1, background:"#f1f5f9", color:"#475569", border:"1px solid #e2e8f0", borderRadius:9, padding:12, cursor:"pointer", fontWeight:600, fontSize:13 }}>Cancel</button>
                    <button onClick={() => forceId !== null && permanentDeleteItem(forceId, true)} style={{ flex:1, background:"#7f1d1d", color:"#fff", border:"none", borderRadius:9, padding:12, cursor:"pointer", fontWeight:700, fontSize:13 }}>
                      <i className="fa-solid fa-bolt" style={{ marginRight:6 }}></i>Force Delete Anyway
                    </button>
                  </div>
                </>
              )}

              {forceAction === "softDelete" && (
                <>
                  <h3 style={{ fontSize:18, fontWeight:700, color:"#1e293b", margin:0 }}>Record Is In Use</h3>
                  <div style={{ width:"100%", background:"#fef9c3", border:"1px solid #fde047", borderRadius:10, padding:"12px 16px", textAlign:"left" }}>
                    <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color:"#ca8a04", marginTop:2, flexShrink:0 }}></i>
                      <p style={{ fontSize:13, color:"#713f12", margin:0, lineHeight:1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize:13, color:"#64748b", margin:0 }}>Do you want to <strong style={{ color:"#dc2626" }}>force delete</strong> anyway?</p>
                  <div style={{ display:"flex", gap:10, width:"100%", marginTop:4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }} style={{ flex:1, background:"#f1f5f9", color:"#475569", border:"1px solid #e2e8f0", borderRadius:9, padding:12, cursor:"pointer", fontWeight:600, fontSize:13 }}>Cancel</button>
                    <button onClick={() => forceId !== null && forceSoftDelete(forceId)} style={{ flex:1, background:"#dc2626", color:"#fff", border:"none", borderRadius:9, padding:12, cursor:"pointer", fontWeight:700, fontSize:13 }}>
                      <i className="fa-solid fa-bolt" style={{ marginRight:6 }}></i>Force Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default ActivityAllowedICD;